import { getUserPreferencesLocalStorageKey } from '@af/design-system-docs-ui/user-preferences/local-storage-key';
import type { ThemeColorModes } from '@atlaskit/tokens/theme-color-modes';

/**
 * Where `UserPreferencesProvider` persists the reader's site-wide color mode.
 */
const COLOR_MODE_STORAGE_KEY = getUserPreferencesLocalStorageKey('colorMode');

const isColorMode = (value: string | null): value is ThemeColorModes =>
	value === 'light' || value === 'dark' || value === 'auto';

/**
 * The color mode `<AppProvider>` should start from: the reader's stored `light`, `dark` or `auto`,
 * or `auto` when nothing valid is stored, storage is blocked, or there is no storage because this is
 * the server.
 *
 * `auto` is the right fallback because it is what the pre-paint scripts effectively did: with no
 * stored choice they left the OS-resolved mode in place, and `auto` makes `<AppProvider>` resolve
 * the same OS preference itself. Seeding it this way means its first write to `data-color-mode`
 * repeats the painted value instead of correcting it.
 */
export function getInitialColorMode(): ThemeColorModes {
	if (typeof window === 'undefined') {
		return 'auto';
	}

	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- the storage `UserPreferencesProvider` writes to
		const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);

		return isColorMode(stored) ? stored : 'auto';
	} catch {
		return 'auto';
	}
}

/**
 * Applies an explicitly stored `light` or `dark` to the document before first paint.
 *
 * Runs as an inline `<script>` in the prerendered `<head>`, after `@atlaskit/tokens`'s
 * `getSSRAutoScript` has resolved the OS preference, so an explicit choice beats the OS. A stored
 * `auto`, nothing stored, or storage the browser has blocked leaves the OS-resolved mode in place.
 *
 * It is inlined with `Function.prototype.toString`, so its body must be self-contained: nothing
 * from outside it (imports, module constants, helpers) exists where it runs, and no comments,
 * because the SSR bundle strips newlines while keeping comments.
 */
/* eslint-disable @atlaskit/platform/no-direct-web-storage-usage, @atlaskit/platform/no-direct-document-usage -- runs inline before paint, where only the globals exist */
function applyStoredColorMode(storageKey: string): void {
	let stored: string | null = null;

	try {
		stored = window.localStorage.getItem(storageKey);
	} catch {
		return;
	}

	if (stored === 'light' || stored === 'dark') {
		document.documentElement.setAttribute('data-color-mode', stored);
	}
}
/* eslint-enable @atlaskit/platform/no-direct-web-storage-usage, @atlaskit/platform/no-direct-document-usage */

/**
 * `applyStoredColorMode` as an inline `<script>` body for the prerendered `<head>`.
 */
export function getStoredColorModeScript(): string {
	return `(${applyStoredColorMode.toString()})(${JSON.stringify(COLOR_MODE_STORAGE_KEY)});`;
}
